import React, { useState, useEffect, useMemo, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import {
  FaArrowLeft, FaUsers, FaMapMarkerAlt, FaClock, FaTrash, FaCheckCircle, FaExclamationTriangle,
  FaSearch, FaEdit, FaTimes, FaPlus, FaLayerGroup, FaList, FaToggleOn,
  FaToggleOff, FaRedo, FaStar, FaStickyNote
} from 'react-icons/fa';
import './CreateCustomerPage.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const REPEAT_OPTIONS = ['None', 'Daily', 'Weekly', 'Custom'];

const emptyForm = {
  company_names: [],
  branch_names: [],
  timing_name: '',
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  items: [],
  categories: [],
  is_active: true,
  repeat: 'None',
  days_of_week: [],
  priority: 1,
  notes: '',
};

export default function MenuTimingPage() {
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const navigate = useNavigate();
  const url = baseUrl || '';

  const [formData, setFormData] = useState(emptyForm);
  const [timings, setTimings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [itemGroups, setItemGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [warningMessage, setWarningMessage] = useState("");
  const [warningType, setWarningType] = useState("");

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [timingToEdit, setTimingToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState(emptyForm);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timingToDelete, setTimingToDelete] = useState(null);

  useEffect(() => {
    fetchTimings();
    fetchItems();
    fetchItemGroups();
    fetchCompanies();
    fetchBranches();
  }, []);

  const fetchTimings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/menu_timings`, { headers: getHeaders() });
      setTimings(res.data || []);
    } catch (err) {
      setError('Failed to fetch menu timings.');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${url}/api/items`, { headers: getHeaders() });
      setItems(res.data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const fetchItemGroups = async () => {
    try {
      const res = await axios.get(`${url}/api/item-groups`, { headers: getHeaders() });
      setItemGroups(res.data || []);
    } catch (err) {
      console.error('Error fetching item groups:', err);
    }
  };

  // Build item options with images
  const itemOptions = items.map(item => {
    let imageUrl = item.image || '';
    if (imageUrl && imageUrl.startsWith('/')) imageUrl = `${url}${imageUrl}`;
    else if (imageUrl && !imageUrl.startsWith('http')) imageUrl = `${url}/api/images/${imageUrl}`;
    return { value: item._id || item.id, label: item.item_name || item.name, image: imageUrl };
  });

  // Build category options from item groups
  const categoryOptions = itemGroups.map(g => ({
    value: g._id || g.id || g.name,
    label: g.name || g.item_group_name || g.group_name || String(g._id)
  }));

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${url}/api/company-details`, { headers: getHeaders() });
      const data = res.data;
      let compArray = Array.isArray(data) ? data : (data?.companyDetails || []);
      if (!Array.isArray(compArray)) compArray = [];
      setCompanies(compArray);
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${url}/api/branches?company_name=All`, { headers: getHeaders() });
      const data = res.data;
      let branchArray = Array.isArray(data) ? data : (data?.branches || []);
      if (!Array.isArray(branchArray)) branchArray = [];
      setBranches(branchArray);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranches([]);
    }
  };

  const uniqueComps = [...new Set((Array.isArray(companies) ? companies : []).map(c => c.company_name).filter(Boolean))];
  const companyOptions = uniqueComps.map(name => ({ value: name, label: name }));

  const rawBranches = (Array.isArray(branches) ? branches : []).map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name || '')).filter(Boolean);
  const uniqueBranches = [...new Set(rawBranches)];
  const branchOptions = uniqueBranches.map(name => ({ value: name, label: name }));

  const filteredTimings = useMemo(() => {
    if (!timings) return [];
    return timings.filter(t => t.timing_name?.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [timings, searchTerm]);

  // Generic form state handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDayToggle = (day, isEdit = false) => {
    const setter = isEdit ? setEditFormData : setFormData;
    setter(prev => {
      const days = prev.days_of_week || [];
      return { ...prev, days_of_week: days.includes(day) ? days.filter(d => d !== day) : [...days, day] };
    });
  };

  const submitData = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    const payload = {
      ...formData,
      company: formData.company_names && formData.company_names.length > 0 ? formData.company_names[0] : (localStorage.getItem('active_company') || 'All'),
      branch: formData.branch_names && formData.branch_names.length > 0 ? formData.branch_names[0] : (localStorage.getItem('active_branch') || 'All'),
      company_names: formData.company_names,
      branch_names: formData.branch_names,
    };
    try {
      await axios.post(`${url}/api/menu_timings`, payload, { headers: getHeaders() });
      setWarningMessage('Menu Timing created successfully!');
      setWarningType('success');
      setFormData(emptyForm);
      fetchTimings();
    } catch (err) {
      setWarningMessage(`Failed to save: ${err.response?.data?.error || err.message}`);
      setWarningType('error');
    } finally {
      setLoading(false);
    }
  };

  const triggerSave = (e, isEdit = false) => {
    e.preventDefault();
    const dataToCheck = isEdit ? editFormData : formData;
    if (!dataToCheck.timing_name || !dataToCheck.start_time || !dataToCheck.end_time) {
      setError('Please fill in required fields: Timing Name, Start Time, End Time.');
      return;
    }
    setError(null);
    setPendingAction(isEdit ? 'edit' : 'create');
    setShowAssignmentModal(true);
  };

  const confirmAndSave = () => {
    setShowAssignmentModal(false);
    if (pendingAction === 'edit') {
      submitEditData();
    } else {
      submitData();
    }
  };

  const openDeleteModal = (id) => { setTimingToDelete(id); setShowDeleteModal(true); };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      await axios.delete(`${url}/api/menu_timings/${id}`, { headers: getHeaders() });
      setSuccess('Menu Timing deleted successfully');
      fetchTimings();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete timing');
    } finally {
      setLoading(false); setShowDeleteModal(false); setTimingToDelete(null);
    }
  };

  const openEditModal = (timing) => {
    setTimingToEdit(timing);
    setEditFormData({
      timing_name: timing.timing_name || '',
      company_names: timing.company_names || [],
      branch_names: timing.branch_names || [],
      start_date: timing.start_date || '',
      end_date: timing.end_date || '',
      start_time: timing.start_time || '',
      end_time: timing.end_time || '',
      items: timing.items || [],
      categories: timing.categories || [],
      is_active: timing.is_active !== false,
      repeat: timing.repeat || 'None',
      days_of_week: timing.days_of_week || [],
      priority: timing.priority || 1,
      notes: timing.notes || '',
    });
    setShowEditModal(true);
  };

  const submitEditData = async () => {
    try {
      setLoading(true);
      await axios.put(`${url}/api/menu_timings/${timingToEdit._id}`, editFormData, { headers: getHeaders() });
      setWarningMessage('Timing updated successfully');
      setWarningType('success');
      setShowEditModal(false);
      fetchTimings();
    } catch (err) {
      setWarningMessage(err.response?.data?.error || 'Failed to update timing');
      setWarningType('error');
    } finally {
      setLoading(false);
    }
  };

  // ─── Style Tokens ───────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #e2e8f0', fontSize: '14px', outline: 'none',
    background: '#fff', color: '#2c3e50', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', marginBottom: '7px', fontWeight: '600', fontSize: '13px', color: '#374151' };
  const sectionDivider = { borderTop: '1.5px dashed #e2e8f0', margin: '24px 0' };
  const sectionTitle = {
    fontSize: '13px', fontWeight: '700', color: '#667eea', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base, borderRadius: '10px', padding: '3px', fontSize: '14px',
      border: state.isFocused ? '1.5px solid #667eea' : '1.5px solid #e2e8f0',
      boxShadow: 'none', '&:hover': { borderColor: '#667eea' }
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#eef2ff', borderRadius: '8px', padding: '1px 4px' }),
    multiValueRemove: (base) => ({ ...base, '&:hover': { backgroundColor: '#fee2e2', color: '#e53e3e' } }),
    option: () => ({}),
    menu: (base) => ({ ...base, borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.12)', overflow: 'hidden', zIndex: 9999 }),
  };

  // ─── Custom react-select Components ─────────────────────────────────────────
  const CustomOption = (props) => (
    <div
      {...props.innerProps}
      style={{
        display: 'flex', alignItems: 'center', padding: '9px 14px', cursor: 'pointer',
        backgroundColor: props.isFocused ? '#eef2ff' : 'white', borderBottom: '1px solid #f1f2f6'
      }}
    >
      {props.data.image ? (
        <img src={props.data.image} alt="" style={{ width: '34px', height: '34px', objectFit: 'cover', borderRadius: '8px', marginRight: '12px', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
      ) : (
        <div style={{ width: '34px', height: '34px', borderRadius: '8px', marginRight: '12px', background: '#e2e8f0', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🍽️</div>
      )}
      <span style={{ fontWeight: '500', color: '#2c3e50', fontSize: '14px' }}>{props.data.label}</span>
    </div>
  );

  const CustomMultiValueLabel = (props) => (
    <div style={{ display: 'flex', alignItems: 'center', padding: '2px 4px' }}>
      {props.data.image && (
        <img src={props.data.image} alt="" style={{ width: '18px', height: '18px', objectFit: 'cover', borderRadius: '4px', marginRight: '5px', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
      )}
      <span style={{ fontSize: '12px', fontWeight: '600', color: '#2c3e50' }}>{props.data.label}</span>
    </div>
  );

  // ─── Toggle Switch ───────────────────────────────────────────────────────────
  const Toggle = ({ value, onChange }) => (
    <div onClick={onChange} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none' }}>
      <div style={{
        position: 'relative', width: '52px', height: '28px', borderRadius: '14px',
        background: value ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#d1d5db',
        transition: 'background 0.3s'
      }}>
        <div style={{
          position: 'absolute', top: '3px', left: value ? '26px' : '3px',
          width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)', transition: 'left 0.3s'
        }} />
      </div>
      <span style={{ fontWeight: '700', fontSize: '14px', color: value ? '#667eea' : '#6b7280' }}>
        {value ? 'Active' : 'Inactive'}
      </span>
    </div>
  );

  // ─── Repeat Button Group ─────────────────────────────────────────────────────
  const RepeatGroup = ({ value, onChange }) => (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {REPEAT_OPTIONS.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          style={{
            padding: '8px 18px', borderRadius: '25px', border: '1.5px solid',
            borderColor: value === opt ? '#667eea' : '#e2e8f0',
            background: value === opt ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#fff',
            color: value === opt ? '#fff' : '#374151',
            fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s'
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );

  // ─── Days of Week ────────────────────────────────────────────────────────────
  const DaysSelector = ({ selectedDays, onToggle }) => (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {DAYS_OF_WEEK.map(day => {
        const short = day.slice(0, 3);
        const active = selectedDays.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => onToggle(day)}
            style={{
              width: '42px', height: '42px', borderRadius: '50%', border: '1.5px solid',
              borderColor: active ? '#667eea' : '#e2e8f0',
              background: active ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#fff',
              color: active ? '#fff' : '#374151',
              fontWeight: '700', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s'
            }}
            title={day}
          >
            {short}
          </button>
        );
      })}
    </div>
  );

  // ─── Shared Form Fields ──────────────────────────────────────────────────────
  const FormFields = ({ data, onChange, onDayToggle, isEdit = false }) => {
    const setField = (name, val) => {
      if (isEdit) setEditFormData(prev => ({ ...prev, [name]: val }));
      else setFormData(prev => ({ ...prev, [name]: val }));
    };

    return (
      <>
        {/* SECTION 1 — Basic Info */}
        <p style={sectionTitle}><FaClock /> Basic Info</p>

        {/* Row: Timing Name (full width) */}
        <div style={{ marginBottom: '16px' }}>
             <label style={labelStyle}>Timing Name <span style={{ color: '#e53e3e' }}>*</span></label>
             <input
               type="text" name="timing_name" value={data.timing_name}
               onChange={onChange} style={inputStyle} placeholder="e.g. Morning Menu..."
             />
        </div>

        {/* Row: Start Date | End Date */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Start Date</label>
            <input type="date" name="start_date" value={data.start_date} onChange={onChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>End Date</label>
            <input type="date" name="end_date" value={data.end_date} onChange={onChange} style={inputStyle} />
          </div>
        </div>

        {/* Row: Start Time | End Time */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '8px' }}>
          <div>
            <label style={labelStyle}>Start Time <span style={{ color: '#e53e3e' }}>*</span></label>
            <input type="time" name="start_time" value={data.start_time} onChange={onChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>End Time <span style={{ color: '#e53e3e' }}>*</span></label>
            <input type="time" name="end_time" value={data.end_time} onChange={onChange} style={inputStyle} />
          </div>
        </div>

        <hr style={sectionDivider} />
        {/* SECTION 2 — Item Scope */}
        <p style={sectionTitle}><FaList /> Item Scope</p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Select Items for this Menu</label>
            <Select
              isMulti options={itemOptions}
              components={{ Option: CustomOption, MultiValueLabel: CustomMultiValueLabel }}
              onChange={sel => setField('items', sel ? sel.map(o => o.value) : [])}
              value={itemOptions.filter(o => (data.items || []).includes(o.value))}
              placeholder="Search and select individual items..."
              styles={selectStyles}
            />
          </div>
          <div>
            <label style={labelStyle}>Select Categories <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span></label>
            <Select
              isMulti options={categoryOptions}
              onChange={sel => setField('categories', sel ? sel.map(o => o.value) : [])}
              value={categoryOptions.filter(o => (data.categories || []).includes(o.value))}
              placeholder="Apply timing to entire category..."
              styles={selectStyles}
            />
          </div>
        </div>

        <hr style={sectionDivider} />
        {/* SECTION 3 — Schedule */}
        <p style={sectionTitle}><FaRedo /> Schedule &amp; Recurrence</p>

        <div style={{ marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ ...labelStyle, marginBottom: '10px' }}>Status</label>
            <Toggle value={data.is_active} onChange={() => setField('is_active', !data.is_active)} />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Repeat / Recurring</label>
          <RepeatGroup value={data.repeat} onChange={val => setField('repeat', val)} />
        </div>

        {(data.repeat === 'Weekly' || data.repeat === 'Custom') && (
          <div style={{ marginBottom: '8px' }}>
            <label style={labelStyle}>Days of Week</label>
            <DaysSelector selectedDays={data.days_of_week || []} onToggle={day => onDayToggle(day, isEdit)} />
          </div>
        )}

        <hr style={sectionDivider} />
        {/* SECTION 4 — Advanced */}
        <p style={sectionTitle}><FaStar /> Advanced</p>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>
            Priority <span style={{ color: '#9ca3af', fontWeight: '400' }}>(1 = highest, 10 = lowest)</span>
          </label>
          <input
            type="number" name="priority" value={data.priority}
            onChange={onChange} min="1" max="10" style={{ ...inputStyle, maxWidth: '140px' }}
            placeholder="1"
          />
          <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '5px' }}>
            When two timings overlap, the one with the lower priority number wins.
          </p>
        </div>

        <div>
          <label style={labelStyle}><FaStickyNote style={{ marginRight: '5px' }} /> Notes / Description <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span></label>
          <textarea
            name="notes" value={data.notes} onChange={onChange} rows={3}
            style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }}
            placeholder="Internal notes about this timing..."
          />
        </div>
      </>
    );
  };

  if (configLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#667eea' }}>
          <FaClock style={{ fontSize: '48px', marginBottom: '16px' }} />
          <p style={{ fontSize: '18px' }}>Loading Menu Timings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      <div className="customer-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="header-left" style={{ flex: 1 }}>
          <button onClick={() => navigate(-1)} className="header-back-btn" disabled={loading}><FaArrowLeft /> Back</button>
        </div>
        <div className="header-center" style={{ flex: 2, textAlign: 'center' }}>
          <h1 style={{ margin: 0 }}>Menu Timings</h1>
        </div>
        <div className="header-right" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={(e) => triggerSave(e, false)} className="premium-btn" disabled={loading} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {loading ? 'Saving...' : <><FaCheckCircle /> Create Timing</>}
          </button>
        </div>
      </div>
      <div className="header-divider" />

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ width: '100%', maxWidth: '1020px', margin: '0 auto' }}>

          {/* Alerts */}
          {error && (
            <div style={{ background: 'linear-gradient(135deg,#ffebee,#ffcdd2)', color: '#c0392b', padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #e74c3c', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaExclamationTriangle /> {error}
              <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b' }}><FaTimes /></button>
            </div>
          )}
          {success && (
            <div style={{ background: 'linear-gradient(135deg,#d4edda,#c8e6c9)', color: '#155724', padding: '14px 18px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #28a745', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaCheckCircle style={{ color: '#27ae60' }} /> {success}
              <button onClick={() => setSuccess(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#155724' }}><FaTimes /></button>
            </div>
          )}

          {/* CREATE FORM */}
          <div style={{ marginBottom: '30px' }}>
            <div className="form-section-card theme-blue">
              <div className="section-header">
                <div className="section-icon"><FaClock /></div>
                <h2>Create Menu Timing</h2>
              </div>
              <div style={{ padding: '28px' }}>
                <FormFields
                  data={formData}
                  onChange={handleChange}
                  onDayToggle={handleDayToggle}
                  isEdit={false}
                />

              </div>
            </div>
          </div>

          {/* EXISTING TIMINGS */}
          <div className="form-section-card theme-green">
            <div className="section-header">
              <div className="section-icon"><FaSearch /></div>
              <h2>Existing Timings ({filteredTimings.length})</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text" placeholder="Search timings by name..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle, maxWidth: '320px' }}
                />
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="premium-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th>Timing Name</th>
                      <th>Date Range</th>
                      <th>Time Slot</th>
                      <th>Repeat</th>
                      <th>Items</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}>Loading...</td></tr>
                    )}
                    {!loading && filteredTimings.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                        <FaClock style={{ fontSize: '32px', marginBottom: '10px', opacity: 0.3 }} />
                        <br />No Menu Timings found. Create one above!
                      </td></tr>
                    )}
                    {!loading && filteredTimings.map((timing) => (
                      <tr key={timing._id}>
                        <td style={{ fontWeight: '700', color: '#2c3e50' }}>
                          {timing.timing_name}
                          {timing.notes && <span style={{ display: 'block', fontSize: '11px', color: '#9ca3af', fontWeight: '400', marginTop: '2px' }}>{timing.notes.substring(0, 40)}{timing.notes.length > 40 ? '...' : ''}</span>}
                        </td>
                        <td style={{ fontSize: '13px' }}>
                          {timing.start_date && timing.end_date
                            ? <><span>{timing.start_date}</span><br /><span style={{ color: '#9ca3af' }}>→ {timing.end_date}</span></>
                            : <span style={{ color: '#bbb' }}>—</span>}
                        </td>
                        <td>
                          <span style={{ background: '#eef2ff', color: '#667eea', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            {timing.start_time} – {timing.end_time}
                          </span>
                        </td>
                        <td>
                          {timing.repeat && timing.repeat !== 'None'
                            ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{timing.repeat}</span>
                            : <span style={{ color: '#bbb', fontSize: '12px' }}>Once</span>}
                          {(timing.days_of_week || []).length > 0 && (
                            <div style={{ display: 'flex', gap: '3px', marginTop: '4px', flexWrap: 'wrap' }}>
                              {timing.days_of_week.map(d => (
                                <span key={d} style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', padding: '1px 5px', fontSize: '10px', fontWeight: '700' }}>{d.slice(0, 3)}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{ background: '#f0fdf4', color: '#22c55e', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                            {(timing.items || []).length} items
                          </span>
                          {(timing.categories || []).length > 0 && (
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{timing.categories.length} categor{timing.categories.length === 1 ? 'y' : 'ies'}</div>
                          )}
                        </td>
                        <td>
                          <span style={{ background: '#fff7ed', color: '#c2410c', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
                            P{timing.priority || 1}
                          </span>
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700',
                            background: timing.is_active !== false ? '#dcfce7' : '#fee2e2',
                            color: timing.is_active !== false ? '#16a34a' : '#dc2626',
                          }}>
                            {timing.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button onClick={() => openEditModal(timing)} className="action-btn action-btn-edit" title="Edit"><FaEdit /></button>
                          <button onClick={() => openDeleteModal(timing._id)} className="action-btn action-btn-delete" title="Delete"><FaTrash /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', zIndex: 1100, paddingTop: '40px', paddingBottom: '40px', overflowY: 'auto' }}>
          <div style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '18px', width: '90%', maxWidth: '680px', boxShadow: '0 25px 80px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
                <FaEdit style={{ color: '#667eea' }} /> Edit Menu Timing
              </h3>
              <FaTimes onClick={() => setShowEditModal(false)} style={{ cursor: 'pointer', color: '#7f8c8d', fontSize: '20px' }} />
            </div>
            <form onSubmit={(e) => triggerSave(e, true)}>
              <FormFields
                data={editFormData}
                onChange={(e) => {
                  const { name, value } = e.target;
                  setEditFormData(prev => ({ ...prev, [name]: value }));
                }}
                onDayToggle={handleDayToggle}
                isEdit={true}
              />
              <button type="submit" style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', marginTop: '20px' }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: '#fff', padding: '36px', borderRadius: '16px', width: '90%', maxWidth: '400px', textAlign: 'center' }}>
            <FaExclamationTriangle style={{ fontSize: '52px', color: '#e74c3c', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 10px', color: '#2c3e50' }}>Confirm Deletion</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '26px' }}>Are you sure you want to delete this menu timing? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ padding: '11px 24px', background: '#f1f2f6', color: '#2f3542', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={() => handleDelete(timingToDelete)} style={{ padding: '11px 24px', background: 'linear-gradient(135deg,#e74c3c,#c0392b)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      
      {/* CUSTOMER ASSIGNMENTS MODAL */}
      {showAssignmentModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '24px', width: '90%', maxWidth: '520px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', animation: 'slideUp 0.3s ease' }}>
            
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: '700' }}>Customer Assignments</h3>
              <button onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes /></button>
            </div>
            
            {/* Modal Body */}
            <div style={{ padding: '32px 24px' }}>
              
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#604BE8', fontSize: '20px', margin: '0 0 16px 0', fontWeight: '700' }}>
                  <FaUsers /> Select Company
                </h4>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  {companyOptions.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>No companies found.</p>
                  ) : (
                    companyOptions.map((comp, idx) => {
                      const currentData = pendingAction === 'edit' ? editFormData : formData;
                      const isSelected = (currentData.company_names || []).includes(comp.value);
                      
                      const toggleSelection = () => {
                        const setter = pendingAction === 'edit' ? setEditFormData : setFormData;
                        setter(prev => {
                          const currentNames = prev.company_names || [];
                          return {
                            ...prev,
                            company_names: isSelected ? currentNames.filter(c => c !== comp.value) : [...currentNames, comp.value]
                          };
                        });
                      };

                      return (
                        <div key={idx} onClick={toggleSelection} style={{ display: 'flex', alignItems: 'center', padding: '10px 18px', background: isSelected ? '#eff2f9' : '#f8fafc', border: `1.5px solid ${isSelected ? '#604BE8' : '#e2e8f0'}`, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            readOnly
                            style={{ width: '18px', height: '18px', accentColor: '#604BE8', marginRight: '10px', pointerEvents: 'none' }}
                          />
                          <span style={{ fontWeight: '600', color: '#334155', fontSize: '14px' }}>{comp.label}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#06D6A0', fontSize: '20px', margin: '0 0 16px 0', fontWeight: '700' }}>
                  <FaMapMarkerAlt /> Select Branch
                </h4>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                  {branchOptions.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>No branches found.</p>
                  ) : (
                    branchOptions.map((branch, idx) => {
                      const currentData = pendingAction === 'edit' ? editFormData : formData;
                      const isSelected = (currentData.branch_names || []).includes(branch.value);
                      
                      const toggleSelection = () => {
                        const setter = pendingAction === 'edit' ? setEditFormData : setFormData;
                        setter(prev => {
                          const currentNames = prev.branch_names || [];
                          return {
                            ...prev,
                            branch_names: isSelected ? currentNames.filter(b => b !== branch.value) : [...currentNames, branch.value]
                          };
                        });
                      };

                      return (
                        <div key={idx} onClick={toggleSelection} style={{ display: 'flex', alignItems: 'center', padding: '10px 18px', background: isSelected ? '#ecfdf5' : '#f8fafc', border: `1.5px solid ${isSelected ? '#06D6A0' : '#e2e8f0'}`, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            readOnly
                            style={{ width: '18px', height: '18px', accentColor: '#06D6A0', marginRight: '12px', pointerEvents: 'none' }}
                          />
                          <span style={{ fontWeight: '600', color: '#334155', fontSize: '15px' }}>{branch.label}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Confirm Button */}
              <button onClick={confirmAndSave} style={{ width: '100%', padding: '16px', background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff', border: 'none', borderRadius: '16px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', boxShadow: '0 10px 15px -3px rgba(124, 58, 237, 0.3)', transition: 'transform 0.2s' }} onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                Confirm & Save
              </button>

            </div>
          </div>
        </div>
      )}

      {/* SUCCESS / ERROR POPUP MODAL */}
      {warningMessage && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ background: '#ffffff', width: '90%', maxWidth: '380px', borderRadius: '24px', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: '84px', height: '84px', borderRadius: '50%', background: warningType === 'success' ? '#ecfdf5' : '#fffbeb', color: warningType === 'success' ? '#06D6A0' : '#FF9F04', border: `2px solid ${warningType === 'success' ? '#a7f3d0' : '#fde68a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '38px', marginBottom: '24px', boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)', zIndex: 1, animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
              {warningType === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '22px', fontWeight: '800', color: '#1e293b', textAlign: 'center', zIndex: 1 }}>
              {warningType === 'success' ? "Success" : "Notice"}
            </h3>
            <p style={{ margin: '0 0 28px 0', fontSize: '15px', color: '#64748b', textAlign: 'center', lineHeight: '1.6', zIndex: 1, padding: '0 10px' }}>
              {warningMessage}
            </p>
            <button onClick={() => setWarningMessage("")} style={{ width: '100%', height: '48px', background: warningType === 'success' ? '#06D6A0' : '#FF9F04', color: '#ffffff', border: 'none', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(255, 159, 4, 0.3)', transition: 'all 0.2s ease' }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

